import { useState } from "react"
import { Plus, Trash2, StickyNote } from "lucide-react"
import { useNotes, useCreateNote, useDeleteNote } from "@/hooks/use-notes"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDate, formatTime } from "@/lib/utils"
import { toast } from "sonner"

interface NotesTabProps {
  entityType: string
  entityId: string
}

export function NotesTab({ entityType, entityId }: NotesTabProps) {
  const { data, isLoading } = useNotes(entityType, entityId)
  const createMutation = useCreateNote()
  const deleteMutation = useDeleteNote()
  const [showForm, setShowForm] = useState(false)
  const [content, setContent] = useState("")

  const notes = data?.data ?? []

  const handleCreate = async () => {
    if (!content.trim()) return
    try {
      await createMutation.mutateAsync({ entityType, entityId, content: content.trim() })
      toast.success("Notatka dodana")
      setContent("")
      setShowForm(false)
    } catch {
      toast.error("Nie udalo sie dodac notatki")
    }
  }

  const handleDelete = async (noteId: string) => {
    if (!confirm("Czy na pewno chcesz usunac te notatke?")) return
    try {
      await deleteMutation.mutateAsync({ entityType, entityId, noteId })
      toast.success("Notatka usunieta")
    } catch {
      toast.error("Nie udalo sie usunac notatki")
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Add note control */}
      <div className="flex items-center justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowForm(!showForm)}
        >
          <Plus size={14} /> Dodaj notatke
        </Button>
      </div>

      {/* Note form */}
      {showForm && (
        <div className="rounded-lg border border-border bg-muted p-3 space-y-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none resize-none"
            placeholder="Tresc notatki..."
            rows={4}
            autoFocus
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowForm(false)
                setContent("")
              }}
            >
              Anuluj
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!content.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Zapisywanie..." : "Zapisz"}
            </Button>
          </div>
        </div>
      )}

      {/* Notes list */}
      {notes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-8 text-muted-foreground">
          <StickyNote size={24} />
          <p className="text-sm">Brak notatek</p>
          {!showForm && (
            <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
              <Plus size={14} /> Dodaj notatke
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="group surface-card p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {/* User initials avatar */}
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {note.user.first_name[0]}
                    {note.user.last_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {note.user.first_name} {note.user.last_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(note.created_at)}, {formatTime(note.created_at)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm whitespace-pre-wrap text-muted-foreground">
                      {note.content}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(note.id)}
                  className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-muted"
                  title="Usun notatke"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
